import React, { useEffect, useState, useCallback } from "react";
import { useMappedState } from "redux-react-hook";
import Layout from "../components/general/Layout";
import {
  Button,
  Card,
  CardTitle,
  CardBody,
  Grid,
  GridItem,
  PageSection,
  PageSectionVariants,
  Title,
  Select,
  SelectOption,
  SelectVariant,
  Divider,
  Spinner,
  EmptyState,
  EmptyStateIcon,
  Bullseye,
  List,
  ListItem,
  Flex,
  FlexItem,
} from "@patternfly/react-core";
import { SearchIcon } from "@patternfly/react-icons";
import { ChartPie, ChartThemeColor } from "@patternfly/react-charts";
import format from "date-fns/format";
import isEmpty from "lodash/isEmpty";
import { fetchPendoData } from "../utils/ajaxUtil";
import PendoConsts from "../constants/pendoConsts";
import { isAdmin, isProductManager } from "../utils/userUtil";
import PageNotFound from "../components/general/PageNotFound";
import UsageProductTableContainer from "./UsageProductTableContainer";

export default () => {
  const mapState = useCallback(
    (state) => ({
      currentUser: state.userState.currentUser,
    }),
    []
  );
  const { currentUser } = useMappedState(mapState);

  const [isFetchingPageData, setIsFetchingPageData] = useState(false);
  const [isFetchingSelectEventData, setIsFetchingSelectEventData] = useState(
    false
  );
  const [isFetchingCloseEventData, setIsFetchingCloseEventData] = useState(
    false
  );
  const [pageData, setPageData] = useState([]);
  const [selectEventData, setSelectEventData] = useState([]);
  const [closeEventData, setCloseEventData] = useState([]);
  const [topSelections, setTopSelections] = useState({
    selectedProduct: [],
    selectedProducts: [],
    topSelectedProduct: [],
    topSelectedProducts: [],
  });
  const [topSelectionsOnClose, setTopSelectionsOnClose] = useState({
    topSelectedProduct: [],
    topSelectedProducts: [],
  });
  const [days, setDays] = useState(7);
  const dateFormat = "MMM d, yyyy";
  const today = new Date();
  const startDay = format(
    new Date(today.getTime() - days * 24 * 60 * 60 * 1000),
    dateFormat
  );
  const endDay = format(
    new Date(today.getTime() - 24 * 60 * 60 * 1000),
    dateFormat
  );

  const options = [
    {
      value: "Last 7 Days",
      disabled: false,
      isPlaceholder: false,
    },
    {
      value: "Last 30 Days",
      disabled: false,
      isPlaceholder: false,
    },
    {
      value: "Last 90 Days",
      disabled: false,
      isPlaceholder: false,
    },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [selected, setSelected] = useState("Last 7 Days");

  useEffect(() => {
    if (
      isFetchingPageData ||
      isFetchingSelectEventData ||
      isFetchingCloseEventData
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [isFetchingPageData, isFetchingSelectEventData, isFetchingCloseEventData]);

  const onToggle = (isOpen) => {
    setIsOpen(isOpen);
  };

  const clearSelection = () => {
    setSelected(null);
    setDays(0);
    setIsOpen(false);
  };

  const onSelect = (event, selection, isPlaceholder) => {
    event.preventDefault();
    if (isPlaceholder) {
      clearSelection();
    } else {
      setSelected(selection);
      setDays(selection.replace(/\D+/g, ""));
      setIsOpen(false);
    }
  };

  const findTopSelections = (eventData) => {
    let reducedData = [];
    // use two dimensional array to save product selections as [productName, numEvents], like
    // [['Red Hat Enterprise Linux', 2], ['Red Hat OpenStack Platform', 1]]
    let selectedProduct = [];
    let selectedProducts = [];

    eventData.forEach((data) => {
      if (
        data &&
        data.properties &&
        data.properties.selectedProducts.length > 0
      ) {
        if (reducedData.length === 0) {
          reducedData.push(data);
          return;
        }
        let index = reducedData.findIndex(
          (e) =>
            e.visitorId === data.visitorId &&
            e.accountId === data.accountId &&
            e.remoteIp
        );
        if (index === -1) {
          reducedData.push(data);
          return;
        }
        if (index !== -1) {
          if (reducedData[index].hour !== data.hour) {
            if (data.hour - reducedData[index].hour <= 60 * 60 * 1000) {
              if (
                reducedData[index].properties.selectedProducts ===
                data.properties.selectedProducts
              ) {
                reducedData[index].numEvents += data.numEvents;
              } else {
                reducedData[index].properties.selectedProducts =
                  data.properties.selectedProducts;
              }
            } else {
              reducedData.push(data);
            }
          }
        }
      }
    });

    reducedData.forEach((data) => {
      let sp = data.properties.selectedProducts;
      if (selectedProduct.length === 0 && selectedProducts.length === 0) {
        selectedProducts.push([sp.join(","), data.numEvents]);
        sp.forEach((e) => selectedProduct.push([e, data.numEvents]));
        return;
      }
      let productsIndex = selectedProducts.findIndex(
        (e) => sp.join(",") === e[0]
      );
      if (productsIndex === -1) {
        selectedProducts.push([sp.join(","), data.numEvents]);
      } else {
        selectedProducts[productsIndex][1] += data.numEvents;
      }
      sp.forEach((i) => {
        let productIndex = selectedProduct.findIndex((e) => i === e[0]);
        if (productIndex === -1) {
          selectedProduct.push([i, data.numEvents]);
        } else {
          selectedProduct[productIndex][1] += data.numEvents;
        }
      });
    });
    selectedProducts.sort((a, b) => (a[1] > b[1] ? -1 : 1));
    selectedProduct.sort((a, b) => (a[1] > b[1] ? -1 : 1));
    return {
      selectedProduct,
      selectedProducts,
      topSelectedProduct: selectedProduct
        .slice(0, 5)
        .map((e) => ({ x: e[0], y: e[1] })),
      topSelectedProducts: selectedProducts
        .slice(0, 5)
        .map((e) => ({ x: e[0], y: e[1] })),
    };
  };

  const onClickSearch = () => {
    setIsFetchingPageData(true);
    fetchPendoData(
      "pageEvents",
      PendoConsts.pageId,
      `now() - ${days} * 24*60*60*1000`,
      "now() - 24*60*60*1000"
    ).then((result) => {
      setPageData(result.results);
      setIsFetchingPageData(false);
    });

    setIsFetchingSelectEventData(true);
    fetchPendoData(
      "trackEvents",
      PendoConsts.selectEventId,
      `now() - ${days} * 24*60*60*1000`,
      "now() - 24*60*60*1000"
    ).then((result) => {
      setSelectEventData(result.results);
      setIsFetchingSelectEventData(false);
      setTopSelections(findTopSelections(result.results));
    });

    setIsFetchingCloseEventData(true);
    fetchPendoData(
      "trackEvents",
      PendoConsts.closeEventId,
      `now() - ${days} *24* 60*60*1000`,
      "now() - 24*60*60*1000"
    ).then((result) => {
      setCloseEventData(result.results);
      setIsFetchingCloseEventData(false);
      setTopSelectionsOnClose(findTopSelections(result.results));
    });
  };

  const emptyData = () => {
    return (
      <Bullseye>
        <EmptyState>
          <EmptyStateIcon icon={SearchIcon} />
          <Title headingLevel="h5" size="lg">
            No results found
          </Title>
        </EmptyState>
      </Bullseye>
    );
  };

  const pageDataCounts = () => {
    let visitors = {};
    let accounts = {};
    if (!isEmpty(pageData)) {
      pageData.forEach((data) => {
        if (data.visitorId && data.visitorId in visitors) {
          visitors[data.visitorId] += data.numEvents;
        } else if (data.visitorId) {
          visitors[data.visitorId] = data.numEvents;
        }
        if (data.accountId && data.accountId in accounts) {
          accounts[data.accountId] += data.numEvents;
        } else if (data.accountId) {
          accounts[data.accountId] = data.numEvents;
        }
      });
    }
    return (
      <List>
        <ListItem>
          Number of visitors: {Object.keys(visitors).length}, total visits by
          visitors: {Object.values(visitors).reduce((a, b) => a + b)}
        </ListItem>
        <ListItem>
          Number of accounts: {Object.keys(accounts).length}, total visits by
          accounts: {Object.values(accounts).reduce((a, b) => a + b)}
        </ListItem>
      </List>
    );
  };

  const renderPageData = () => {
    if (isEmpty(pageData) && !isFetchingPageData) {
      return emptyData();
    } else if (isFetchingPageData) {
      return (
        <Bullseye>
          <Spinner />
        </Bullseye>
      );
    }
    return pageDataCounts();
  };

  const renderChart = (data) => {
    return (
      <Flex direction={{ default: "column" }}>
        <FlexItem>
          Top 5 selected product:
          <div style={{ height: "230px", width: "1200px" }}>
            <ChartPie
              ariaDesc="Top selected product"
              ariaTitle="Top selected product"
              constrainToVisibleArea={true}
              data={data.topSelectedProduct}
              height={230}
              labels={({ datum }) => `${datum.x}: ${datum.y}`}
              legendData={data.topSelectedProduct.map((e) => ({
                name: `${e.x}: ${e.y}`,
              }))}
              legendOrientation="vertical"
              legendPosition="right"
              themeColor={ChartThemeColor.multiOrdered}
              width={1200}
              padding={{
                bottom: 20,
                left: 20,
                right: 140, // Adjusted to accommodate legend
                top: 20,
              }}
            />
          </div>
        </FlexItem>
        <FlexItem>
          Top 5 selected products:
          <div style={{ height: "230px", width: "1200px" }}>
            <ChartPie
              ariaDesc="Top selected products"
              ariaTitle="Top selected products"
              constrainToVisibleArea={true}
              data={data.topSelectedProducts}
              height={230}
              labels={({ datum }) => `${datum.x}: ${datum.y}`}
              legendData={data.topSelectedProducts.map((e) => ({
                name: `${e.x}: ${e.y}`,
              }))}
              legendOrientation="vertical"
              legendPosition="right"
              themeColor={ChartThemeColor.multiOrdered}
              width={1200}
              padding={{
                bottom: 20,
                left: 20,
                right: 140, // Adjusted to accommodate legend
                top: 20,
              }}
            />
          </div>
        </FlexItem>
      </Flex>
    );
  };

  const renderSelectData = () => {
    if (isEmpty(selectEventData) && !isFetchingSelectEventData) {
      return emptyData();
    } else if (isFetchingSelectEventData) {
      return (
        <Bullseye>
          <Spinner />
        </Bullseye>
      );
    }
    return renderChart(topSelections);
  };

  const renderCloseData = () => {
    if (isEmpty(closeEventData) && !isFetchingCloseEventData) {
      return emptyData();
    } else if (isFetchingCloseEventData) {
      return (
        <Bullseye>
          <Spinner />
        </Bullseye>
      );
    }
    return renderChart(topSelectionsOnClose);
  };

  const renderProductsData = () => {
    if (isEmpty(selectEventData) && !isFetchingSelectEventData) {
      return emptyData();
    } else if (isFetchingSelectEventData) {
      return (
        <Bullseye>
          <Spinner />
        </Bullseye>
      );
    }
    return <UsageProductTableContainer data={topSelections.selectedProduct} />;
  };

  const renderBody = () => {
    return (
      <div>
        <PageSection variant={PageSectionVariants.light}>
          <Title
            headingLevel="h1"
            size="4xl"
            className="pf-c-title--PaddingTop"
          >
            Usage Data
          </Title>
          <Divider
            component="div"
            style={{ paddingTop: "1rem", paddingBottom: "1rem" }}
          />
          <Grid hasGutter>
            <GridItem span={4}>
              <Select
                variant={SelectVariant.single}
                aria-label="Select date range"
                onToggle={onToggle}
                onSelect={onSelect}
                selections={selected}
                isOpen={isOpen}
                isDisabled={isDisabled}
              >
                {options.map((option, index) => (
                  <SelectOption
                    isDisabled={option.disabled}
                    key={index}
                    value={option.value}
                    isPlaceholder={option.isPlaceholder}
                  />
                ))}
              </Select>
            </GridItem>
            <GridItem span={4}>
              <Button variant="primary" onClick={onClickSearch}>
                Search
              </Button>
            </GridItem>
          </Grid>
        </PageSection>
        <PageSection>
          <Card isHoverable>
            <CardTitle>
              Number of visitors and accounts ({startDay} - {endDay})
            </CardTitle>
            <CardBody>{renderPageData()}</CardBody>
          </Card>
        </PageSection>
        <PageSection>
          <Card isHoverable>
            <CardTitle>
              Product selection counts ({startDay} - {endDay})
            </CardTitle>
            <CardBody>{renderProductsData()}</CardBody>
          </Card>
        </PageSection>
        <PageSection>
          <Card isHoverable>
            <CardTitle>
              Top 5 selected product(s) ({startDay} - {endDay})
            </CardTitle>
            <CardBody>{renderSelectData()}</CardBody>
          </Card>
        </PageSection>
      </div>
    );
  };

  return (
    <Layout>
      {isAdmin(currentUser.roles) || isProductManager(currentUser.roles) ? (
        renderBody()
      ) : (
        <Bullseye>
          <PageNotFound />
        </Bullseye>
      )}
    </Layout>
  );
};
